import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as S from "./MobileHero.styles";
import { IHeroProps, ITourTooltip } from "./MobileHero.types";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";

import { useTranslation } from "hooks/useTranslation";
import { ImageSkeletonLoader } from "components/ImageSkeletonLoader";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";

enum AppVariant {
  Network = "Network",
  Protect = "Protect",
  Access = "Access",
  Connect = "Connect",
}

export const MobileHero = ({ config }: Readonly<IHeroProps>) => {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = useRef<Record<string, HTMLVideoElement>>({});
  useVideoCleanup(videoRef);
  useVideoCleanup(activeVideoRef);

  const [hasLoadedMainAsset, setHasLoadedMainAsset] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<ITourTooltip>(
    config?.tooltips?.[0],
  );
  const [canOpenTooltip, setCanOpenTooltip] = useState(false);

  const onActiveVideoPlaybackEnd = useCallback(
    (variant: AppVariant) => {
      if (variant !== openTooltip.variant) {
        return;
      }

      setOpenTooltip((prev) => {
        const nextIndex = prev.id + 1;
        const isLastIndex = nextIndex >= config.tooltips.length;
        const correctedIndex = isLastIndex ? 0 : nextIndex;

        return config.tooltips[correctedIndex];
      });
    },
    [config.tooltips, openTooltip.variant],
  );

  const onVideoPlaybackEnd = useCallback(() => {
    if (!config?.tooltips?.length) {
      return;
    }

    setCanOpenTooltip(true);
  }, [config]);

  const onUserTouch = useCallback((tooltip: ITourTooltip) => {
    setOpenTooltip(tooltip);
  }, []);

  const onLoadedMainVideo = useCallback(() => {
    setHasLoadedMainAsset(true);
  }, []);

  useEffect(() => {
    if (!canOpenTooltip) {
      return;
    }

    if (activeVideoRef.current[openTooltip.id]) {
      try {
        activeVideoRef.current[openTooltip.id].currentTime = 0;
        activeVideoRef.current[openTooltip.id].play();
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  }, [canOpenTooltip, openTooltip.id]);

  return (
    <S.HeroContainer>
      <S.Wrapper>
        <S.ActiveVideoWrapper>
          <ImageSkeletonLoader isVisible={!hasLoadedMainAsset} />

          {(config.tooltips ?? []).map((tooltip, index) => (
            <Fragment key={`${index}-${tooltip.id}`}>
              <S.ActiveVideo
                key={`${index}-${tooltip.id}`}
                ref={(element) => (activeVideoRef.current[index] = element)}
                src={tooltip.video}
                muted
                $isActive={tooltip.variant === openTooltip.variant}
                playsInline
                onEnded={() => onActiveVideoPlaybackEnd(tooltip.variant)}
              />

              <S.TooltipTriggerBtn
                $left={tooltip.left}
                $isVisible={canOpenTooltip}
                onTouchStart={() => onUserTouch(tooltip)}
              />

              <S.Tooltip
                $isVisible={
                  canOpenTooltip && openTooltip.variant === tooltip.variant
                }
              >
                <S.TooltipContentWrap>
                  <S.TooltipTitle>{tooltip.variant}</S.TooltipTitle>
                  <S.TooltipDescription>
                    {t(tooltip.description)}
                  </S.TooltipDescription>
                </S.TooltipContentWrap>
              </S.Tooltip>
            </Fragment>
          ))}

          <S.BackgroundVideo
            ref={videoRef}
            src={config?.src}
            muted
            playsInline
            autoPlay
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(config?.poster),
              1920 * 2,
            )}
            $isVisible={!config?.tooltips?.length || !canOpenTooltip}
            onLoadedData={onLoadedMainVideo}
            onEnded={onVideoPlaybackEnd}
          />
        </S.ActiveVideoWrapper>
      </S.Wrapper>
    </S.HeroContainer>
  );
};
